import { forwardRef, type PropsWithChildren } from "react";

import CloseIcon from "$/lib/icons/CloseIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { TagProps } from "./Tag.types";

const Tag = forwardRef<HTMLDivElement, PropsWithChildren<TagProps>>(
  (
    {
      label,
      disabled = false,
      onRemove,
      className,
      textColor,
      backgroundColor,
      dotColor,
      hideDot = false,
      style,
      ...tagProps
    },
    forwardedRef,
  ) => {
    const resolvedStyle = { ...style };
    if (textColor) {
      resolvedStyle.color = textColor;
    }
    if (backgroundColor) {
      resolvedStyle.backgroundColor = backgroundColor;
    }

    const resolvedDotColor = dotColor ?? textColor ?? "#00A67E";

    return (
      <div
        ref={forwardedRef}
        aria-disabled={disabled}
        className={cn(
          "inline-flex items-center gap-2 rounded-full bg-[#0F1013] px-3 py-1.5 text-xs font-semibold text-[#E8E8F0] transition-all duration-200 ",
          { "cursor-not-allowed opacity-60": disabled },
          className,
        )}
        style={resolvedStyle}
        {...tagProps}
      >
        <span>{label}</span>
        {!hideDot && (
          <span
            aria-hidden
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: resolvedDotColor }}
          />
        )}
        {onRemove && (
          <button
            type="button"
            className="flex items-center text-current"
            onClick={onRemove}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    );
  },
);

Tag.displayName = "Tag";

export default Tag;
